import type {
  DesktopEnvironmentBootstrapEnvelope,
  ExecutionEnvironmentRuntimeTopology,
} from "@cmmd-forge/contracts";
import {
  normalizeExecutionEnvironmentCapabilities,
  type KnownEnvironment,
} from "@cmmd-forge/client-runtime";
import { isLoopbackUrl } from "@cmmd-forge/shared/loopback";
import { isElectron } from "../env";
import { readEnvironmentBootstrapEnvelope } from "./bootstrapEnvelope";
import { readPrimaryEnvironmentTarget } from "./primary/target";
import { readPrimaryEnvironmentDescriptor } from "./primary/context";

export type ForgeRuntimeSurface =
  | "cloud-official"
  | "desktop-local"
  | "desktop-cloud"
  | "local-web-dev";

export type ForgeRuntimeViewOverride =
  | "auto"
  | "cloud-official"
  | "desktop-local"
  | "local-web-dev";

export type ForgeRuntimeHostKind = "user-cloud-vm" | "desktop-local" | "dev-local-web";

export type ForgeWorkSource = "desktop-local" | "cloud-vm" | "cmmd" | "github" | "cloud";
export type ForgeExecutionRuntime = "cloud" | "desktop-local" | "dev-local-web";
export type ForgeModelRuntime = "local" | "cloud" | "unknown";
export type ForgeLinkState = "local-only" | "cloud-only" | "linked" | "unlinked";

export const FORGE_RUNTIME_VIEW_OVERRIDE_KEY = "forge_runtime_view_override";

export function readRuntimeViewOverride(): ForgeRuntimeViewOverride {
  if (!import.meta.env.DEV) return "auto";
  try {
    const raw = window.localStorage.getItem(FORGE_RUNTIME_VIEW_OVERRIDE_KEY);
    return isForgeRuntimeViewOverride(raw) ? raw : "auto";
  } catch {
    return "auto";
  }
}

export function writeRuntimeViewOverride(value: ForgeRuntimeViewOverride): void {
  if (!import.meta.env.DEV) return;
  try {
    if (value === "auto") {
      window.localStorage.removeItem(FORGE_RUNTIME_VIEW_OVERRIDE_KEY);
    } else {
      window.localStorage.setItem(FORGE_RUNTIME_VIEW_OVERRIDE_KEY, value);
    }
  } catch {
    // localStorage unavailable.
  }
}

function isForgeRuntimeViewOverride(value: unknown): value is ForgeRuntimeViewOverride {
  return (
    value === "auto" ||
    value === "cloud-official" ||
    value === "desktop-local" ||
    value === "local-web-dev"
  );
}

export interface RuntimeSurfaceInput {
  readonly isElectron: boolean;
  readonly locationOrigin: string;
  readonly primarySource: KnownEnvironment["source"] | null;
  readonly primaryTopology?: ExecutionEnvironmentRuntimeTopology | null;
  readonly desktopBootstrap: DesktopEnvironmentBootstrapEnvelope | null;
  readonly override?: ForgeRuntimeViewOverride;
}

export interface RuntimeSurfaceState {
  readonly surface: ForgeRuntimeSurface;
  readonly hostKind: ForgeRuntimeHostKind;
  readonly executionRuntime: ForgeExecutionRuntime;
  readonly modelRuntime: ForgeModelRuntime;
  readonly override: ForgeRuntimeViewOverride;
  readonly isDevOverrideActive: boolean;
}

export function deriveRuntimeSurface(input: RuntimeSurfaceInput): RuntimeSurfaceState {
  const override = input.override ?? "auto";

  if (override === "cloud-official") {
    return {
      surface: "cloud-official",
      hostKind: "user-cloud-vm",
      executionRuntime: "cloud",
      modelRuntime: "cloud",
      override,
      isDevOverrideActive: true,
    };
  }
  if (override === "desktop-local") {
    return {
      surface: "desktop-local",
      hostKind: "desktop-local",
      executionRuntime: "desktop-local",
      modelRuntime: "local",
      override,
      isDevOverrideActive: true,
    };
  }
  if (override === "local-web-dev") {
    return {
      surface: "local-web-dev",
      hostKind: "dev-local-web",
      executionRuntime: "dev-local-web",
      modelRuntime: "unknown",
      override,
      isDevOverrideActive: true,
    };
  }

  if (input.desktopBootstrap?.primaryMode === "cloud") {
    return {
      surface: "desktop-cloud",
      hostKind: "user-cloud-vm",
      executionRuntime: "cloud",
      modelRuntime: "cloud",
      override,
      isDevOverrideActive: false,
    };
  }

  if (
    input.primaryTopology === "cloud-shell" ||
    input.primaryTopology === "cmmd-control-plane" ||
    input.primaryTopology === "cmmd-cloud-runtime"
  ) {
    return {
      surface: input.isElectron ? "desktop-cloud" : "cloud-official",
      hostKind: "user-cloud-vm",
      executionRuntime: "cloud",
      modelRuntime: "cloud",
      override,
      isDevOverrideActive: false,
    };
  }

  if (input.isElectron) {
    return {
      surface: "desktop-local",
      hostKind: "desktop-local",
      executionRuntime: "desktop-local",
      modelRuntime: "local",
      override,
      isDevOverrideActive: false,
    };
  }

  if (isLoopbackUrl(input.locationOrigin) && import.meta.env.DEV) {
    return {
      surface: "local-web-dev",
      hostKind: "dev-local-web",
      executionRuntime: "dev-local-web",
      modelRuntime: input.primarySource === "paired-bearer" ? "local" : "unknown",
      override,
      isDevOverrideActive: false,
    };
  }

  return {
    surface: "cloud-official",
    hostKind: "user-cloud-vm",
    executionRuntime: "cloud",
    modelRuntime: "cloud",
    override,
    isDevOverrideActive: false,
  };
}

export function readRuntimeSurfaceState(): RuntimeSurfaceState {
  const desktopBootstrap = readEnvironmentBootstrapEnvelope();
  const primaryTarget = readPrimaryEnvironmentTarget();
  const primaryDescriptor = readPrimaryEnvironmentDescriptor();
  return deriveRuntimeSurface({
    isElectron,
    locationOrigin: window.location.origin,
    primarySource: primaryTarget?.source ?? null,
    primaryTopology: primaryDescriptor
      ? normalizeExecutionEnvironmentCapabilities(primaryDescriptor).runtime.topology
      : null,
    desktopBootstrap,
    override: readRuntimeViewOverride(),
  });
}
